import React from "react";
import { Link, withRouter } from "react-router";
import queryString from "query-string";
import classNames from "classnames";

import AuthMixin from "../../mixins/authenticationMixin";
import AppStore from "../../stores/stores";
import AppRoutes from "../../routes/routes";
import DataError from "../errors/data-error";
import LocalLoading from "../../components/loading/local-loading";
import ExploreIcon from "./explore-icon";
import api from "../../api/api";

import "./explore-all.scss";

class ExploreAll extends React.Component {
  constructor() {
    super();

    this.state = {
      data: [],
      loading: true,
      open: false,
    };

    this._scrollTo = this._scrollTo.bind(this);
  }

  /**
   * slugs of the ?open= target and every ancestor of it, so those nodes
   * render expanded
   */
  _openPath() {
    const target = this.state.open;
    if (!target) {
      return new Set();
    }

    const find = (nodes, path) => {
      for (const node of nodes || []) {
        const nodePath = [...path, node.slug];
        if (node.slug === target) {
          return nodePath;
        }
        const found = find(node.chapters || node.topics || node.checklists, nodePath);
        if (found) {
          return found;
        }
      }
      return null;
    };

    return new Set(find(this.state.data, []) || []);
  }

  _scrollTo(e) {
    if (e) {
      e.preventDefault();
    }

    if (!this.state.open) {
      return;
    }

    let el = document.getElementById(this.state.open);

    if (el) {
      api.scrollTo(el);
    } else {
      window.scrollTo(0, 0);
    }
  }

  resolveSearch(location, params) {
    const { search } = location;
    let parsed = queryString.parse(search);
    this.setState({
      open: (params && params.id) || parsed.open || false,
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.location.search !== this.props.location.search ||
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      this.resolveSearch(this.props.location, this.props.params);
      setTimeout(this._scrollTo, 200);
    }
  }

  componentDidMount() {
    this.resolveSearch(this.props.location, this.props.params);

    AppStore.getExploreTree(this.props.userApiRequest, {
      onSuccess: ({ data }) => {
        this.setState({ data, loading: false });
        setTimeout(this._scrollTo, 200);
      },
      onError: () => this.setState({ error: true, loading: false }),
    });
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="explore-tree">
          <LocalLoading text="Loading..." />
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div className="explore-tree">
          <DataError />
        </div>
      );
    }

    const openPath = this._openPath();

    return (
      <div className="explore-tree">
        <ul className="explore-parts">
          {this.state.data.map((part) => {
            const checklistCount = part.chapters.reduce(
              (total, chapter) =>
                total +
                chapter.topics.reduce((sum, topic) => sum + topic.checklists.length, 0),
              0
            );

            return (
              <ExploreNode
                key={part.slug}
                id={part.slug}
                className="explore-part"
                current={openPath.has(part.slug)}
                header={
                  <React.Fragment>
                    <ExploreIcon cat={part} />
                    <span className="explore-part-text">
                      <span className="explore-part-title">{part.title}</span>
                      <span className="explore-part-meta">
                        {pluralise(part.chapters.length, 'chapter')} · {pluralise(checklistCount, 'checklist')}
                      </span>
                    </span>
                  </React.Fragment>
                }
              >
                <ul className="explore-chapters">
                  {part.chapters.map((chapter) => (
                    <ExploreNode
                      key={chapter.slug}
                      id={chapter.slug}
                      className="explore-chapter"
                      current={openPath.has(chapter.slug)}
                      header={
                        <React.Fragment>
                          <span className="explore-chapter-title">{chapter.title}</span>
                          <span className="explore-count">{pluralise(chapter.topics.length, 'topic')}</span>
                        </React.Fragment>
                      }
                    >
                      <ul className="explore-topics">
                        {chapter.topics.map((topic) => (
                          <ExploreNode
                            key={topic.slug}
                            id={topic.slug}
                            className="explore-topic"
                            current={openPath.has(topic.slug)}
                            header={
                              <React.Fragment>
                                <span className="explore-topic-title">{topic.title}</span>
                                <span className="explore-count">{topic.checklists.length}</span>
                              </React.Fragment>
                            }
                          >
                            <ul className="explore-checklists">
                              {topic.checklists.map((checklist) => (
                                <li key={checklist.slug} id={checklist.slug}>
                                  <Link to={`${AppRoutes.checklists}${checklist.slug}`}>
                                    {checklist.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </ExploreNode>
                        ))}
                      </ul>
                    </ExploreNode>
                  ))}
                </ul>
              </ExploreNode>
            );
          })}
        </ul>
      </div>
    );
  }
}

const pluralise = (count, noun) => `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

/**
 * accordion item for the explore tree. children stay in the DOM while
 * collapsed so every checklist link remains crawlable
 */
class ExploreNode extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: !!props.current };
    this.toggleOpen = this.toggleOpen.bind(this);
  }

  componentDidUpdate(prevProps) {
    // open when this becomes the ?open= target after mounting
    if (this.props.current && !prevProps.current && !this.state.open) {
      this.setState({ open: true });
    }
  }

  toggleOpen() {
    this.setState(({ open }) => ({ open: !open }));
  }

  render() {
    const { id, className, current, header, children } = this.props;
    const { open } = this.state;

    return (
      <li id={id} className={classNames(className, { 'is-open': open, 'is-current': current })}>
        <button type="button" className="explore-toggle" aria-expanded={open} onClick={this.toggleOpen}>
          {header}
          <svg className="explore-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
        <div className="explore-body" hidden={!open}>{children}</div>
      </li>
    );
  }
}

export default withRouter(AuthMixin(ExploreAll));
